import ClassNames from "classnames";
import { EmblaCarouselType } from "embla-carousel";
import { FunctionComponent, useEffect, useRef, useState } from "react";
import { SlideImage } from "yet-another-react-lightbox";
import { selectActiveSite } from "../selectors/sites.js";
import { useAppSelector } from "../store/hooks.js";
import styles from "../stylesheets/components/image-gallery-layout-2.module.css";
import moduleBaseStyles from "../stylesheets/components/module-base.module.css";
import {
  ColorScheme,
  ContainerQueries,
  ImageModuleSettings,
  Language,
  TitleAndSubtitleTextAlign,
  TranslatedModule,
} from "../types/index.js";
import { css } from "../utils/emotion.js";
import { useDidMountEffect } from "../utils/hooks.js";
import {
  getGalleryMediaAspectRatio,
  getInlineSize,
  getPlaceholderImage,
} from "../utils/utils.js";
import { minWidthBreakpoints } from "./ContainerQueries.js";
import ModuleHeadings from "./ModuleHeadings.js";
import ImageModule from "./Modules/ImageModule.js";
import Slider from "./Slider.js";
import SliderArrow from "./SliderArrow.js";

interface Props {
  title: string;
  subtitle: string;
  textAlign: TitleAndSubtitleTextAlign;
  scheme: ColorScheme;
  isFirstOnPage: boolean;
  imageModules: TranslatedModule<ImageModuleSettings>[];
  queries: ContainerQueries;
  slides: SlideImage[];
  isPreview: boolean;
  languageId: Language;
  pageId: string;
  moduleId: string;
  activeModuleId: string | undefined;
  onClickImage: (index: number) => void;
}

const breakPoints = {
  [minWidthBreakpoints["Query--small"]]: 1,
  [minWidthBreakpoints["Query--medium"]]: 2,
  [minWidthBreakpoints["Query--large"]]: 3,
  [minWidthBreakpoints["Query--xxlarge"]]: 4,
  [1920]: 5,
  [2560]: 6,
};

const ImageGalleryLayout2: FunctionComponent<Props> = ({
  title,
  subtitle,
  textAlign,
  isFirstOnPage,
  scheme,
  imageModules,
  slides,
  isPreview,
  languageId,
  pageId,
  moduleId,
  activeModuleId,
  onClickImage,
}) => {
  const [slider, setSlider] = useState<EmblaCarouselType>();
  const [slideIndex, setSlideIndex] = useState(0);
  const ref = useRef<HTMLDivElement>(null);
  const [slidesToShow, setSlidesToShow] = useState(1);
  const rafId = useRef<number>(undefined);

  const site = useAppSelector(selectActiveSite);

  useDidMountEffect(() => {
    if (!slider) return;

    const handleSlideChange = (slideApi: EmblaCarouselType) => {
      setSlideIndex(slideApi.selectedScrollSnap());
    };
    slider?.on("select", handleSlideChange);
    return () => {
      slider?.off("select", handleSlideChange);
    };
  }, [slider]);

  useEffect(() => {
    const onResize = (entries: ResizeObserverEntry[]) => {
      const inlineSize = getInlineSize(entries);
      if (inlineSize === undefined) return;

      const slidesToShow = Object.entries(breakPoints).reduce(
        (acc, [breakPoint, value]) =>
          inlineSize >= Number(breakPoint) ? value : acc,
        1,
      );
      setSlidesToShow(slidesToShow);
    };

    const observer = new ResizeObserver((entries) => {
      rafId.current = window.requestAnimationFrame(() => onResize(entries));
    });

    ref.current && observer.observe(ref.current);

    return () => {
      rafId.current !== undefined && window.cancelAnimationFrame(rafId.current);
      observer.disconnect();
    };
  }, []);

  const images = imageModules.length
    ? imageModules
    : new Array(5)
        .fill(true)
        .map((_, index) =>
          getPlaceholderImage({
            siteId: site.id,
            pageId,
            languageId,
            parentId: moduleId,
            index,
          }),
        );

  return (
    <div className={styles.ImageGalleryLayout2} ref={ref}>
      <ModuleHeadings
        textAlign={textAlign}
        scheme={scheme}
        isFirstOnPage={isFirstOnPage}
        title={title}
        subtitle={subtitle}
        wrapperClassName={ClassNames(
          moduleBaseStyles.Module__Wrapper,
          moduleBaseStyles["Module__Wrapper--small-padding"],
          styles.ImageGalleryLayout2__Headings,
        )}
      />
      <div
        className={ClassNames(
          moduleBaseStyles.Module__Wrapper,
          moduleBaseStyles["Module__Wrapper--small-padding"],
          styles.ImageGalleryLayout2__SliderWrapper,
        )}
      >
        <Slider
          className={styles.ImageGalleryLayout2__Slider}
          containerClassName={styles.ImageGalleryLayout2__SliderContainer}
          slideClassName={styles.ImageGalleryLayout2__Slide}
          isPreview={isPreview}
          sliderEffect="slide"
          onSliderLoaded={setSlider}
          slidesToShow={slidesToShow}
          activePreviewSlideId={activeModuleId}
          slideIds={images.map(({ id }) => id)}
          loop={false}
        >
          {images.map((imageModule, index) => {
            const item = slides[index];
            return (
              <a
                href={isPreview || !item ? undefined : item.src}
                className={styles.ImageGalleryLayout2__Item}
                key={imageModule.id}
                onClick={(e) => {
                  e.preventDefault();
                  onClickImage(index);
                }}
              >
                <ImageModule
                  aspectRatio={getAspectRatio(index)}
                  sizes="50vw"
                  lazyLoad={true}
                  width={1920}
                  translatedModule={imageModule}
                  isPreview={isPreview}
                  isSlider={false}
                  pageId={pageId}
                  scheme={scheme}
                  showOverlay={false}
                />
              </a>
            );
          })}
        </Slider>
        {slidesToShow < images.length && (
          <div>
            <SliderArrow
              className={ClassNames(styles.ImageGalleryLayout2__SliderArrow, {
                [styles["ImageGalleryLayout2__SliderArrow--disabled"]]:
                  slideIndex === 0,
              })}
              iconClassName={ClassNames(
                styles.ImageGalleryLayout2__SliderArrowIcon,
                css({ fill: scheme.main.text }),
              )}
              glyph="arrow-back"
              direction="left"
              onClick={() => slider?.scrollPrev()}
              scheme={scheme}
              transparentBackground
              languageId={languageId}
            />
            <SliderArrow
              className={ClassNames(styles.ImageGalleryLayout2__SliderArrow, {
                [styles["ImageGalleryLayout2__SliderArrow--disabled"]]:
                  slideIndex === images.length - slidesToShow,
              })}
              iconClassName={ClassNames(
                styles.ImageGalleryLayout2__SliderArrowIcon,
                css({ fill: scheme.main.text }),
              )}
              glyph="arrow-forward"
              direction="right"
              onClick={() => slider?.scrollNext()}
              scheme={scheme}
              transparentBackground
              languageId={languageId}
            />
          </div>
        )}
      </div>
    </div>
  );
};

const getAspectRatio = (index: number): number =>
  getGalleryMediaAspectRatio(index, "layout-2");

export default ImageGalleryLayout2;
